import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  FormLabel,
  CircularProgress,
} from "@mui/material";
import { colors } from "../../constants/Colors";
import useIsMobile from "../../hooks/useIsMobile";
import { updatePathRouteKey } from "../../api/CompanyAPI";
import { getCompanyData } from "../../utils/generalUtil";
import { getCompanyById } from "../../api/CompanyAPI";
import { CompanyType } from "../../types/generalTypes";

interface CustomLinkStepProps {
  onComplete: (company: CompanyType) => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const CustomLinkStep = ({
  onComplete,
  onError,
  onSuccess,
}: CustomLinkStepProps) => {
  const isMobile = useIsMobile();
  const [customLink, setCustomLink] = useState("");
  const [loading, setLoading] = useState(false);

  const validateCustomLink = (link: string) => {
    const regex = /^[a-z0-9-]+$/;
    return regex.test(link) && link.length >= 3 && link.length <= 20;
  };

  const handleSaveCustomLink = async () => {
    if (!customLink.trim()) {
      onError("Digite seu link personalizado");
      return;
    }

    if (!validateCustomLink(customLink)) {
      onError(
        "Link deve conter apenas letras minúsculas, números e hífens (3-20 caracteres)"
      );
      return;
    }

    setLoading(true);
    try {
      const companyData = getCompanyData();
      const result = await updatePathRouteKey(
        companyData?.companyId || 0,
        customLink
      );

      if (result.success) {
        onSuccess(result.message || "Link personalizado salvo com sucesso!");
        const updatedCompany = await getCompanyById(
          companyData?.companyId?.toString() ?? ""
        );
        if (updatedCompany) {
          onComplete(updatedCompany);
        }
      } else {
        onError(
          result.message ||
            "Erro ao salvar link personalizado. Tente novamente."
        );
      }
    } catch (error) {
      onError("Erro ao salvar link personalizado. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography
        sx={{
          fontSize: isMobile ? "13px" : "14px",
          color: "#666",
          marginBottom: isMobile ? "12px" : "16px",
        }}
      >
        Este será o link da sua página de agendamento pública
      </Typography>
      <FormControl fullWidth sx={{ marginBottom: isMobile ? "12px" : "16px" }}>
        <FormLabel sx={{ fontSize: isMobile ? "14px" : "16px" }}>Seu link personalizado</FormLabel>
        <Box sx={{ 
          display: "flex", 
          alignItems: "flex-start", 
          gap: isMobile ? "4px" : "8px",
          flexDirection: isMobile ? "column" : "row"
        }}>
          <Typography
            sx={{
              color: "#666",
              fontSize: isMobile ? "14px" : "16px",
              fontWeight: 500,
              marginTop: isMobile ? "0px" : "8px",
              lineHeight: "1.5",
              flexShrink: 0,
            }}
          >
            anotareservas.com/
          </Typography>
          <TextField
            value={customLink}
            onChange={(e) => setCustomLink(e.target.value.toLowerCase())}
            placeholder="exemplo-quadra"
            variant="outlined"
            disabled={loading}
            fullWidth
            size={isMobile ? "small" : "medium"}
            sx={{ 
              flex: 1,
              "& .MuiOutlinedInput-root": {
                height: isMobile ? "48px" : "40px",
              },
              "& .MuiInputBase-input": {
                paddingTop: isMobile ? "12px" : "8px",
                paddingBottom: isMobile ? "12px" : "8px",
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSaveCustomLink();
              }
            }}
            helperText="Apenas letras minúsculas, números e hífens (3-20 caracteres)"
          />
        </Box>
      </FormControl>
      <Button
        variant="contained"
        onClick={handleSaveCustomLink}
        disabled={
          loading || !customLink.trim() || !validateCustomLink(customLink)
        }
        fullWidth={isMobile}
        sx={{
          backgroundColor: colors.blue,
          "&:hover": {
            backgroundColor: colors.darkBlue,
          },
          fontWeight: 550,
          padding: isMobile ? "12px" : "8px 16px",
          fontSize: isMobile ? "16px" : "14px",
        }}
      >
        {loading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          "Salvar Link"
        )}
      </Button>
    </Box>
  );
};

export default CustomLinkStep;
